import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { useAuth } from '@/contexts/AuthContext';
import { StaffBadge } from '@/components/StaffBadge';

function formatSADate(): string {
  return new Intl.DateTimeFormat('en-US', {
    timeZone: 'Africa/Johannesburg',
    month: 'short',
    day: 'numeric',
  }).format(new Date());
}

// v4.0 - With role badge
export function Header() {
  const [currentDate, setCurrentDate] = useState(formatSADate());
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const location = useLocation();
  const { userBusiness } = useAuth();

  const isAdmin = userBusiness?.role === 'ADMIN' || userBusiness?.role === 'SUPER_ADMIN';

  // Get page title from route
  const getPageTitle = () => {
    const path = location.pathname;
    if (path === '/') return 'Today';
    if (path === '/inventory') return 'Inventory';
    if (path === '/inventory/new') return 'Add Product';
    if (path.startsWith('/inventory/')) return 'Edit Product';
    if (path === '/reports') return 'Reports';
    if (path === '/settings') return 'Settings';
    return 'Leafstock';
  };

  useEffect(() => {
    // Update date every minute (not every second - unnecessary)
    const timer = setInterval(() => setCurrentDate(formatSADate()), 60000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  return (
    <header
      className="fixed top-0 left-0 right-0 bg-dark/95 backdrop-blur-sm border-b border-primary/20 z-40"
      style={{ paddingTop: 'env(safe-area-inset-top)' }}
    >
      <div className="flex items-center justify-between py-3 px-4">
        {/* Left: Page Title + Role Badge */}
        <div className="flex items-center gap-2">
          <h1 className="text-lg font-bold text-white">
            {getPageTitle()}
          </h1>
          {isAdmin && (
            <span className="px-2 py-0.5 bg-primary/20 text-primary text-xs font-bold rounded-full">
              ADMIN
            </span>
          )}
        </div>

        {/* Right: Staff Badge + Date + Status */}
        <div className="flex items-center gap-3">
          <StaffBadge />

          <span className="text-sm text-gray-400">
            {currentDate}
          </span>

          {/* Connection Status Dot */}
          <div className="flex items-center gap-1.5">
            <div className={`w-2 h-2 rounded-full ${isOnline ? 'bg-success' : 'bg-error animate-pulse'}`} />
            <span className={`text-xs font-medium ${isOnline ? 'text-success' : 'text-error'}`}>
              {isOnline ? 'Live' : 'Offline'}
            </span>
          </div>
        </div>
      </div>
    </header>
  );
}
